<template>
    <div class="d-flex flex-column justify-space-between h-100">
        <LoadingOverlay :model-value="submitting" />
        <v-card-title>{{ $t("addPaymentIncident.title") }}</v-card-title>
        <v-card-text>
            <v-form ref="form" @submit.prevent="submitIncident">
                <IncidentCompanySelect
                    v-model="incident.memberId"
                    :rules="requiredRule"
                />
                <v-textarea
                    data-cy="incident-description"
                    v-model="incident.description"
                    :label="$t('addPaymentIncident.description')"
                    rows="3"
                    required
                    :rules="requiredRule"
                />
                <!-- A short note about required docs -->
                <p class="mb-4 text-body-2">
                    {{ $t("incidentDocuments.requiredDocsNote") }}
                </p>
                <!-- show chips for each uploaded file -->
                <IncidentFileInput
                    v-model="incident.documents"
                    :minFiles="4"
                    :label="$t('addPaymentIncident.documents')"
                    accept=".pdf,.jpg,.jpeg,.png"
                />

                <v-text-field
                    data-cy="incident-pending-value"
                    v-model="incident.pendingValue"
                    :label="$t('addPaymentIncident.pendingValue')"
                    type="number"
                    min="0"
                    required
                    :rules="requiredRule"
                />
                <v-select
                    data-cy="incident-currency"
                    v-model="incident.currency"
                    :label="$t('addPaymentIncident.currency')"
                    :items="currencies"
                    required
                    :rules="requiredRule"
                />
                <v-date-input
                    data-cy="incident-deadline"
                    v-model="incident.deadline"
                    :label="$t('addPaymentIncident.deadline')"
                    variant="outlined"
                    full-width
                    :rules="requiredRule"
                />
            </v-form>
        </v-card-text>
    </div>
</template>

<script>
import axios from "axios";
import { mapMutations } from "vuex";
import emitter from "@/eventBus";
import { showAlertModal } from "@/utils";
import IncidentCompanySelect from "@/components/forms/IncidentCompanySelect.vue";
import IncidentFileInput from "@/components/forms/IncidentFileInput.vue";
import LoadingOverlay from "@/components/display/LoadingOverlay.vue";
import dayjs from "dayjs";

export default {
    name: "AddPaymentIncident",
    components: {
        IncidentCompanySelect,
        IncidentFileInput,
        LoadingOverlay,
    },
    data() {
        return {
            submitting: false,
            incident: {
                memberId: "",
                description: "",
                documents: [], // doc upload
                pendingValue: null,
                currency: "",
                deadline: null,
            },
            currencies: ["EUR", "RON", "USD"],
        };
    },
    mounted() {
        this.SET_FORM_BUTTONS([
            {
                label: "common.save", // i18n key
                event: "save-payment-incident",
                color: "primary",
                dataCy: 'payment-incident-save',
            },
        ]);
        emitter.on("save-payment-incident", this.submitIncident);
        emitter.on("language-changed", this.revalidateForm);
    },
    beforeUnmount() {
        emitter.off("language-changed", this.revalidateForm);
        emitter.off("save-payment-incident", this.submitIncident);
        this.RESET_FORM_BUTTONS();
    },
    computed: {
        requiredRule() {
            return [(v) => !!v || this.$t("validation.required")];
        },
    },
    methods: {
        ...mapMutations(["SET_FORM_BUTTONS", "RESET_FORM_BUTTONS"]),
        showAlertModal,

        revalidateForm() {
            this.$refs.form?.validate();
        },
        async submitIncident() {
            const formResult = await this.$refs.form.validate();

            if (!formResult?.valid) {
                return;
            }
            this.submitting = true;
            try {
                // final guard check
                if (this.incident.documents.length < 4) {
                    this.submitting = false;
                    this.showAlertModal(
                        this.$store,
                        this.$t("validation.fourFilesRequired"),
                        "warning",
                        12000
                    );
                    return;
                }

                if (this.incident.deadline instanceof Date) {
    this.incident.deadline = dayjs(this.incident.deadline).format("YYYY-MM-DD");
}

                const formData = new FormData();
                formData.append("companyId", this.incident.memberId);
                formData.append("description", this.incident.description);
                formData.append("pendingValue", this.incident.pendingValue);
                formData.append("currency", this.incident.currency);
                formData.append("deadline", this.incident.deadline);

                for (const file of this.incident.documents) {
                    formData.append("documents", file);
                }

                await axios.post(
                    `${process.env.VUE_APP_API_URL}/payment-incidents/`,
                    formData,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );

                this.showAlertModal(
                    this.$store,
                    this.$t("validation.incidentCreated"),
                    "success",
                    5000
                );
                this.$refs.form.reset();
                this.$router.push("/dashboard/my-incidents");
            } catch (error) {
                console.error("Error submitting incident:", error);

                // Check for 404 from get_object_or_404
                if (error.response && error.response.status === 404) {
                    this.showAlertModal(
                        this.$store,
                        this.$t("validation.companyNotFound"),
                        "danger",
                        12000
                    );
                } else {
                    this.showAlertModal(
                        this.$store,
                        this.$t("validation.incidentError"),
                        "danger",
                        12000
                    );
                }
            } finally {
                this.submitting = false;
            }
        },
    },
};
</script>
